import {
  AlgComponentType,
  UserRequirement,
  UserRequirementType,
} from '@youshift/shared/types';
import { ComponentPrioritySchema } from '@youshift/shared/hooks/mutations';

import {
  ImportanceLevel,
  IMPORTANCE_BRACKETS,
  AlgorithmSettingsModalProps,
  AlgComponentTypeSupportingPriority,
  ImportanceBracket,
  IMPORTANCE_LEVEL_MAX_VALUES,
  UNBREAKABLE_LEVEL_VALUE,
} from './types';

export const getImportanceLevel = (value: number): ImportanceBracket => {
  // First check if the value is UNBREAKABLE
  if (value === UNBREAKABLE_LEVEL_VALUE) {
    return IMPORTANCE_BRACKETS[ImportanceLevel.UNBREAKABLE];
  }

  // Otherwise find the appropriate bracket
  return (
    Object.values(IMPORTANCE_BRACKETS).find(
      (bracket, index, array) =>
        value <= bracket.maxValue &&
        (index === 0 || value > array[index - 1].maxValue),
    ) || IMPORTANCE_BRACKETS[ImportanceLevel.UNBREAKABLE]
  );
};

export const isUnbreakable = (value: number): boolean =>
  value === UNBREAKABLE_LEVEL_VALUE;

export const getImportanceValue = (level: ImportanceLevel): number => {
  if (level === ImportanceLevel.UNBREAKABLE) {
    return UNBREAKABLE_LEVEL_VALUE;
  }
  return IMPORTANCE_BRACKETS[level].minValue;
};

type DeepRecord = {
  [key: string]:
    | number
    | number[]
    | string
    | number
    | boolean
    | null
    | DeepRecord;
};

export const countTotalLeafs = (obj: DeepRecord): number => {
  let count = 0;

  for (const value of Object.values(obj)) {
    if (typeof value === 'object' && value !== null) {
      count += countTotalLeafs(value as DeepRecord);
    } else {
      count++;
    }
  }

  return count;
};

export const getConstraintCountByType = (
  importanceValues: ComponentPrioritySchema,
  type: AlgComponentTypeSupportingPriority,
) => countTotalLeafs(importanceValues[type]);

// ----------------------------------------------------------------------------
// UserReq Utils
// ----------------------------------------------------------------------------
export const getMinComponentTypeFromUserReq = (userReq: UserRequirement) => {
  switch (userReq.req_type) {
    case UserRequirementType.DURATION:
      return AlgComponentType.C_USER_SHIFT_REQUIREMENT_RULE_MIN_DURATION;
    case UserRequirementType.SLOTS:
      return AlgComponentType.C_USER_SHIFT_REQUIREMENT_RULE_MIN_SLOTS;
    case UserRequirementType.COUNTER_UNIT:
      return AlgComponentType.C_USER_SHIFT_REQUIREMENT_RULE_MIN_COUNTER_UNIT;
    default:
      throw new Error(`Unknown user requirement type: ${userReq.req_type}`);
  }
};

export const getMaxComponentTypeFromUserReq = (userReq: UserRequirement) => {
  switch (userReq.req_type) {
    case UserRequirementType.DURATION:
      return AlgComponentType.C_USER_SHIFT_REQUIREMENT_RULE_MAX_DURATION;
    case UserRequirementType.SLOTS:
      return AlgComponentType.C_USER_SHIFT_REQUIREMENT_RULE_MAX_SLOTS;
    case UserRequirementType.COUNTER_UNIT:
      return AlgComponentType.C_USER_SHIFT_REQUIREMENT_RULE_MAX_COUNTER_UNIT;
    default:
      throw new Error(`Unknown user requirement type: ${userReq.req_type}`);
  }
};

export const getReqValueFromUserReq = (
  algComponentType: AlgComponentType,
  userReq: UserRequirement,
) => {
  switch (algComponentType) {
    case AlgComponentType.C_USER_SHIFT_REQUIREMENT_RULE_MIN_DURATION:
      return userReq.min_duration;
    case AlgComponentType.C_USER_SHIFT_REQUIREMENT_RULE_MIN_SLOTS:
      return userReq.min_slots;
    case AlgComponentType.C_USER_SHIFT_REQUIREMENT_RULE_MIN_COUNTER_UNIT:
      return userReq.min_counter_unit;
    case AlgComponentType.C_USER_SHIFT_REQUIREMENT_RULE_MAX_DURATION:
      return userReq.max_duration;
    case AlgComponentType.C_USER_SHIFT_REQUIREMENT_RULE_MAX_SLOTS:
      return userReq.max_slots;
    case AlgComponentType.C_USER_SHIFT_REQUIREMENT_RULE_MAX_COUNTER_UNIT:
      return userReq.max_counter_unit;
    default:
      throw new Error(`Unknown user requirement type: ${userReq.req_type}`);
  }
};
